import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../Apiurl";
import { BlockSkeleton, TextSkeleton } from "../interfaccia/loading";
import "./R2ImagesDashboardPage.css";

const PER_PAGE = 20;
const THUMBNAIL_CONCURRENCY = 4;
const EMPTY_FILTERS = { productCode: "", createdBy: "" };

function normalizeFilters(filters) {
  return {
    productCode: String(filters?.productCode || "").trim(),
    createdBy: String(filters?.createdBy || "").trim(),
  };
}

function buildListParams(page, filters) {
  return {
    page,
    perPage: PER_PAGE,
    ...(filters.productCode ? { productCode: filters.productCode } : {}),
    ...(filters.createdBy ? { createdBy: filters.createdBy } : {}),
  };
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("it-IT");
}

function DashboardSkeleton() {
  return (
    <div className="r2-images-dashboard__list" aria-label="Caricamento immagini R2">
      {Array.from({ length: 7 }).map((_, index) => (
        <div className="r2-images-dashboard__row" key={index}>
          <TextSkeleton lines={2} width="190px" lastLineWidth="110px" tone="soft" />
          <TextSkeleton lines={2} width="150px" lastLineWidth="90px" tone="soft" />
          <TextSkeleton lines={2} width="170px" lastLineWidth="120px" tone="soft" />
          <div className="r2-images-dashboard__images">
            {Array.from({ length: 4 }).map((__, imageIndex) => (
              <BlockSkeleton
                width={92}
                height={92}
                borderRadius={10}
                tone="soft"
                key={imageIndex}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function revokeUrls(urls) {
  urls.forEach((url) => URL.revokeObjectURL(url));
}

function getRowImages(row) {
  const images = Array.isArray(row?.images)
    ? row.images.filter((image) => image?.assetId && image?.url)
    : [];
  if (images.length) return images;
  return row?.cover?.assetId && row.cover.url
    ? [{ ...row.cover, position: row.cover.position ?? 1 }]
    : [];
}

function thumbnailKey(row, image) {
  return `${row.productCode}:${image.assetId}`;
}

function R2ImagesDashboardPage() {
  const { getAccessTokenSilently } = useAuth0();
  const requestIdRef = useRef(0);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [selectionFilters, setSelectionFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, perPage: PER_PAGE, total: 0, totalPages: 1 });
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const createdUrls = [];

    const isCurrent = () => requestIdRef.current === requestId && !controller.signal.aborted;

    async function load() {
      setLoading(true);
      setError("");
      setThumbnails({});
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${apiUrl}/moda/images-library`, {
          headers,
          params: buildListParams(page, selectionFilters),
          signal: controller.signal,
        });
        if (!isCurrent()) return;
        const nextRows = Array.isArray(response.data?.data) ? response.data.data : [];
        setRows(nextRows);
        setMeta(response.data?.meta ?? { page, perPage: PER_PAGE, total: 0, totalPages: 1 });
        setLoading(false);

        const thumbnailJobs = nextRows.flatMap((row) =>
          getRowImages(row).map((image) => ({ row, image })),
        );
        let nextIndex = 0;
        async function thumbnailWorker() {
          while (nextIndex < thumbnailJobs.length && isCurrent()) {
            const { row, image } = thumbnailJobs[nextIndex];
            nextIndex += 1;
            const key = thumbnailKey(row, image);
            try {
              const binary = await axios.get(`${apiUrl}${image.url}`, {
                headers,
                responseType: "blob",
                signal: controller.signal,
              });
              if (!isCurrent()) return;
              const objectUrl = URL.createObjectURL(binary.data);
              createdUrls.push(objectUrl);
              setThumbnails((current) => ({
                ...current,
                [key]: objectUrl,
              }));
            } catch (thumbnailError) {
              if (axios.isCancel(thumbnailError) || controller.signal.aborted) return;
              if (isCurrent()) {
                setThumbnails((current) => ({
                  ...current,
                  [key]: null,
                }));
              }
            }
          }
        }
        await Promise.all(
          Array.from(
            { length: Math.min(THUMBNAIL_CONCURRENCY, thumbnailJobs.length) },
            () => thumbnailWorker(),
          ),
        );
      } catch (requestError) {
        if (axios.isCancel(requestError) || controller.signal.aborted || !isCurrent()) return;
        setRows([]);
        setError(
          requestError.response?.data?.error ||
            requestError.message ||
            "Riepilogo immagini R2 non disponibile.",
        );
        setLoading(false);
      }
    }

    load();
    return () => {
      controller.abort();
      revokeUrls(createdUrls);
    };
  }, [getAccessTokenSilently, page, refreshKey, selectionFilters]);

  function applyFilters(event) {
    event.preventDefault();
    const nextFilters = normalizeFilters(filterDraft);
    setFilterDraft(nextFilters);
    setPage(1);
    setSelectionFilters(nextFilters);
  }

  function resetFilters() {
    const nextFilters = { ...EMPTY_FILTERS };
    setFilterDraft(nextFilters);
    setPage(1);
    setSelectionFilters(nextFilters);
  }

  const latestAt = useMemo(() => rows[0]?.createdAt ?? null, [rows]);

  return (
    <main className="r2-images-dashboard">
      <section
        className="r2-images-dashboard__header"
        aria-labelledby="r2-images-dashboard-title"
      >
        <div>
          <p className="r2-images-dashboard__eyebrow">Archivio immagini prodotto</p>
          <h1 id="r2-images-dashboard-title">Dashboard immagini R2</h1>
          <p className="r2-images-dashboard__scope">
            Una riga per articolo, ordinata dall’ultima revisione corrente archiviata su R2.
          </p>
        </div>
        <button
          type="button"
          className="r2-images-dashboard__refresh"
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={loading}
        >
          Aggiorna
        </button>
      </section>

      <form className="r2-images-dashboard__filters" onSubmit={applyFilters}>
        <label>
          <span>Codice VS</span>
          <input
            type="search"
            value={filterDraft.productCode}
            onChange={(event) => {
              const { value } = event.target;
              setFilterDraft((current) => ({ ...current, productCode: value }));
            }}
            placeholder="Cerca codice VS"
          />
        </label>
        <label>
          <span>Utente</span>
          <input
            type="search"
            value={filterDraft.createdBy}
            onChange={(event) => {
              const { value } = event.target;
              setFilterDraft((current) => ({ ...current, createdBy: value }));
            }}
            placeholder="Nome o email"
          />
        </label>
        <div className="r2-images-dashboard__filter-actions">
          <button type="submit" className="r2-images-dashboard__filter-submit">
            Filtra
          </button>
          <button
            type="button"
            className="r2-images-dashboard__filter-reset"
            onClick={resetFilters}
          >
            Azzera
          </button>
        </div>
      </form>

      <section className="r2-images-dashboard__summary" aria-label="Riepilogo archivio R2">
        <div>
          <span>Articoli in archivio</span>
          <strong>{Number(meta.total || 0).toLocaleString("it-IT")}</strong>
        </div>
        <div>
          <span>Ultimo caricamento visibile</span>
          <strong>{formatDate(latestAt)}</strong>
        </div>
        <div>
          <span>Pagina</span>
          <strong>{meta.page} / {meta.totalPages}</strong>
        </div>
      </section>

      {error ? <p className="r2-images-dashboard__error" role="alert">{error}</p> : null}
      {loading ? <DashboardSkeleton /> : null}
      {!loading && !rows.length && !error ? (
        <p className="r2-images-dashboard__empty">Nessuna revisione immagini presente su R2.</p>
      ) : null}

      {!loading && rows.length ? (
        <section className="r2-images-dashboard__list" aria-label="Ultime immagini R2">
          <div className="r2-images-dashboard__columns" aria-hidden="true">
            <span>Articolo</span>
            <span>Revisione</span>
            <span>Archiviazione</span>
          </div>
          {rows.map((row, index) => {
            const images = getRowImages(row);
            return (
              <article
                className="r2-images-dashboard__row r2-images-dashboard__row--loaded"
                style={{ "--r2-row-index": index }}
                key={row.revisionId}
              >
                <div className="r2-images-dashboard__product">
                  <strong>{row.productCode}</strong>
                  <span>{row.imageCount} {row.imageCount === 1 ? "immagine" : "immagini"}</span>
                </div>
                <div className="r2-images-dashboard__revision">
                  <strong>v{row.version} · {row.status}</strong>
                  <span>{row.changeType}</span>
                </div>
                <div className="r2-images-dashboard__audit">
                  <strong>{formatDate(row.createdAt)}</strong>
                  <span>{row.createdBy}</span>
                </div>
                <div
                  className="r2-images-dashboard__images"
                  aria-label={`Immagini R2 dell’articolo ${row.productCode}`}
                >
                  {images.map((image, imageIndex) => {
                    const key = thumbnailKey(row, image);
                    const position = image.position ?? imageIndex + 1;
                    return (
                      <div className="r2-images-dashboard__cover" key={image.assetId}>
                        {Object.prototype.hasOwnProperty.call(thumbnails, key) ? (
                          thumbnails[key] ? (
                            <img
                              src={thumbnails[key]}
                              alt={`Immagine ${position} R2 dell’articolo ${row.productCode}`}
                              title={image.originalFilename || undefined}
                            />
                          ) : (
                            <span>Immagine non disponibile</span>
                          )
                        ) : (
                          <BlockSkeleton width={92} height={92} borderRadius={10} tone="soft" />
                        )}
                      </div>
                    );
                  })}
                  {!images.length ? <span>Nessuna immagine disponibile</span> : null}
                </div>
              </article>
            );
          })}
        </section>
      ) : null}

      {!loading && meta.totalPages > 1 ? (
        <nav className="r2-images-dashboard__pagination" aria-label="Paginazione immagini R2">
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            disabled={page <= 1}
          >
            Precedente
          </button>
          <span>{page} di {meta.totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(meta.totalPages, value + 1))}
            disabled={page >= meta.totalPages}
          >
            Successiva
          </button>
        </nav>
      ) : null}
    </main>
  );
}

export default R2ImagesDashboardPage;
